import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { getAllCategoryGroupsHashedByType } from 'utilities/getCategoryGroupsByType';
import amountExpression from 'utilities/ledger/amountExpression';
import matchCategories, { categoryChoicesForMonths } from 'utilities/ledger/categorySuggestions';

/**
 * The input that appears inside a cell being edited.
 *
 * Deliberately a bare `<input>` rather than the app's `TextInput`: that one renders a materialize
 * floating label and calls `M.updateTextFields()`, neither of which has anywhere to go inside a
 * cell six pixels tall. Same for `CurrencyInput`, which wraps it.
 *
 * A commit reports the direction the key that ended it wants the cursor to go, because Enter and
 * Tab differ there and only the editor knows which one was pressed. So does an abandoned edit:
 * Escape stays put, but Enter on a value that turned out not to have changed should still drop to
 * the next row -- nothing was wrong with it, it just does not need posting.
 */
export default function LedgerCellEditor({ type, value, initial, selectedDates, onCommit, onCancel }) {
	const inputRef = useRef(null);
	const settled = useRef(false);
	const [draft, setDraft] = useState(() => startingDraft(type, value, initial));

	useEffect(() => {
		const input = inputRef.current;
		if(!input) {
			return;
		}
		input.focus();
		// Typing into a selected cell replaces it, the way it does in a spreadsheet, so the caret
		// belongs after the character that started the edit. F2 and a double-click select the
		// whole value instead, which is what makes the next keystroke replace it. Only the text
		// ones: `select()` on `<input type=date>` throws, its value not being text at all.
		if(initial === null && type !== 'date') {
			input.select();
		}
	}, []);

	/** @param commitWith {*} the value to write, or `undefined` to leave the cell as it was */
	function settle(commitWith, direction) {
		if(settled.current) {
			return;
		}
		settled.current = true;
		if(commitWith === undefined) {
			onCancel(direction);
		} else {
			onCommit(commitWith, direction);
		}
	}

	function commitDraft(direction) {
		// An amount that does not read as a number is a typo, not an instruction to zero the cell,
		// and staying in place is what says so.
		settle(readDraft(type, draft), direction);
	}

	function onKeyDown(event) {
		if(event.key === 'Enter') {
			event.preventDefault();
			commitDraft('down');
		} else if(event.key === 'Tab') {
			event.preventDefault();
			commitDraft(event.shiftKey ? 'left' : 'right');
		} else if(event.key === 'Escape') {
			event.preventDefault();
			settle(undefined, null);
		}
	}

	if(type === 'category') {
		return (
			<CategoryEditor
				inputRef={inputRef}
				value={value}
				initial={initial}
				selectedDates={selectedDates}
				onSettle={settle}
			/>
		);
	}

	return (
		<input
			ref={inputRef}
			className='ledger_cell_editor'
			type={type === 'date' ? 'date' : 'text'}
			inputMode={type === 'money' ? 'decimal' : undefined}
			value={draft}
			autoComplete='off'
			onChange={(event) => setDraft(event.target.value)}
			onKeyDown={onKeyDown}
			onBlur={() => commitDraft(null)}
		/>
	);
}

/** What the input shows the moment it opens. */
function startingDraft(type, value, initial) {
	if(initial !== null && type !== 'date') {
		return initial;
	}
	switch(type) {
		case 'money':
			return value === null || value === undefined ? '' : (value / 100).toFixed(2);
		case 'date':
			return value ? value.format('YYYY-MM-DD') : '';
		default:
			return value === null || value === undefined ? '' : String(value);
	}
}

/** The model value a draft means, or `undefined` for "this is not a value -- leave the cell alone". */
function readDraft(type, draft) {
	switch(type) {
		case 'money': {
			if(draft.trim() === '') {
				return 0;
			}
			const cents = amountExpression(draft);
			return cents === null ? undefined : cents;
		}
		case 'date': {
			if(draft.trim() === '') {
				return undefined;
			}
			const date = moment(draft, 'YYYY-MM-DD', true);
			return date.isValid() ? date : undefined;
		}
		default:
			return draft;
	}
}

/**
 * The category picker: a text box with the matches under it, the way a spreadsheet does it.
 *
 * Not the app's `CategorySelect`, which is react-select -- in a cell it opens a floating menu the
 * width of the page over a table that is already dense, and brings keyboard handling that fights
 * the grid's. Not a native `<select>` either, which was the first attempt: it type-jumps without
 * ever showing what was typed, so with sixty categories and two called Jasmine you are guessing at
 * which one the third J landed on. Seeing the query and the list it narrows to is the whole point.
 *
 * Opening does not filter. F2 on a cell is "show me the options", and starting the list already
 * cut down to the one category that is in the cell would hide every other one behind a backspace.
 * The first keystroke is what turns it into a search -- and typing into a selected cell replaces
 * what was there, so that keystroke arrives on its own with the old name gone.
 *
 * "Unknown" is one of the options, at the top, the same way it is in the app's other category
 * selects -- see `noCategoryChoice`. Emptying the box clears the cell too, but only somebody who
 * has been told that knows it; an entry she can see and arrow onto is how the rest of the app says
 * the same thing.
 */
function CategoryEditor({ inputRef, value, initial, selectedDates, onSettle }) {
	const listRef = useRef(null);
	const choices = useMemo(
		() => categoryChoicesForMonths(getAllCategoryGroupsHashedByType(), selectedDates),
		[selectedDates]
	);
	// A cell with no category holds `null`, `undefined` or -- on a transaction saved before the
	// column meant anything -- `0`, and all three are the blank choice rather than a category to
	// look up. Its own id is `null`, so falsy is the whole test.
	const currentId = value || null;
	const current = useMemo(
		() => (currentId ? choices.find((choice) => choice.id === currentId) : null) || null,
		[choices, currentId]
	);

	const [draft, setDraft] = useState(initial !== null ? initial : (current ? current.name : ''));
	const [filtering, setFiltering] = useState(initial !== null);
	const [highlighted, setHighlighted] = useState(() => {
		const index = choices.findIndex((choice) => choice.id === currentId);
		return initial !== null || index < 0 ? 0 : index;
	});
	const [dropUp, setDropUp] = useState(false);

	const matches = useMemo(
		() => (filtering ? matchCategories(choices, draft) : choices),
		[choices, draft, filtering]
	);

	// Keeping the highlighted row on screen as the arrow keys walk past the bottom of the list.
	useEffect(() => {
		const list = listRef.current;
		const option = list && list.children[highlighted];
		if(option && typeof option.scrollIntoView === 'function') {
			option.scrollIntoView({ block: 'nearest' });
		}
	}, [highlighted, matches]);

	/**
	 * Which way the list opens. The blank entry row lives at the bottom of the block and is where
	 * most of the typing happens, so a list that only ever drops downward is a list she cannot see
	 * exactly when she is using it. Measured rather than assumed, and left dropping down wherever
	 * nothing can be measured -- jsdom and a list that has not been laid out both report zero.
	 */
	useLayoutEffect(() => {
		const input = inputRef.current;
		const list = listRef.current;
		if(!input || !list || typeof input.getBoundingClientRect !== 'function') {
			return;
		}
		const rect = input.getBoundingClientRect();
		const height = list.offsetHeight;
		const viewport = window.innerHeight;
		if(!height || !viewport) {
			return;
		}
		setDropUp(rect.bottom + height > viewport && rect.top > height);
	}, [matches.length]);

	/**
	 * What the cell should end up holding: whatever the highlight is sitting on.
	 *
	 * Emptying the box still clears the category, the same thing Delete on the cell does, and it
	 * needs no rule of its own to do it -- an empty query matches everything, so the highlight lands
	 * back on the blank choice at the top and that choice is `null`.
	 *
	 * Anything else has to land on a real category. There is no typing a new one in here, and
	 * inventing one silently would be worse than doing nothing, so an unmatched name commits
	 * nothing at all.
	 */
	function chosen() {
		return matches.length ? matches[Math.min(highlighted, matches.length - 1)].id : undefined;
	}

	function settleWith(direction) {
		const id = chosen();
		// A name that matches nothing is a typo. Staying in the cell is what says so -- moving on
		// would look exactly like a value that had been accepted.
		if(id === undefined) {
			onSettle(undefined, null);
			return;
		}
		// Nothing changed, so there is nothing to post -- but Enter still means "next row".
		if(id === currentId) {
			onSettle(undefined, direction);
			return;
		}
		onSettle(id, direction);
	}

	function onChange(event) {
		setDraft(event.target.value);
		setFiltering(true);
		setHighlighted(0);
	}

	function onKeyDown(event) {
		if(event.key === 'ArrowDown' && event.altKey) {
			// Excel's gesture for "show me all of them again", after a query has narrowed the list.
			event.preventDefault();
			setFiltering(false);
			setHighlighted(0);
			return;
		}
		if(event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			const step = event.key === 'ArrowDown' ? 1 : -1;
			setHighlighted((index) => Math.min(Math.max(index + step, 0), Math.max(matches.length - 1, 0)));
			return;
		}
		if(event.key === 'Enter') {
			event.preventDefault();
			settleWith('down');
		} else if(event.key === 'Tab') {
			event.preventDefault();
			settleWith(event.shiftKey ? 'left' : 'right');
		} else if(event.key === 'Escape') {
			event.preventDefault();
			onSettle(undefined, null);
		}
	}

	return (
		<div className='ledger_cell_combo'>
			<input
				ref={inputRef}
				className='ledger_cell_editor'
				type='text'
				value={draft}
				autoComplete='off'
				onChange={onChange}
				onKeyDown={onKeyDown}
				onBlur={() => onSettle(undefined, null)}
			/>
			{!!matches.length &&
			<ul
				ref={listRef}
				className={`ledger_cell_suggestions ${dropUp ? 'drop_up' : ''}`}
			>
				{matches.map((choice, index) => (
					<li
						key={choice.blank ? 'no_category' : choice.id}
						className={[
							index === highlighted ? 'highlighted' : '',
							choice.deleted ? 'deleted' : '',
							choice.blank ? 'no_category' : '',
						].filter(Boolean).join(' ')}
						// Without this the input loses focus on the way down and blurs the edit
						// away before the click that picked something ever lands.
						onMouseDown={(event) => event.preventDefault()}
						onMouseEnter={() => setHighlighted(index)}
						onClick={() => onSettle(choice.id, null)}
					>
						<span className='name'>
							{!!choice.parentName &&
							<span className='parent'>{choice.parentName} &#9656; </span>
							}
							{choice.name}
						</span>
						<span className='group'>{choice.deleted ? `${choice.groupName} (deleted)` : choice.groupName}</span>
					</li>
				))}
			</ul>
			}
		</div>
	);
}
